/**
 * Client-side formula estimator — PRESENTATION ONLY.
 *
 * This does NOT replace or alter the backend salary engine or formula parser.
 * It produces a best-effort *preview* of what each derived variable resolves to
 * given a sample Monthly CTC, so the UI can show live allocation percentages
 * while the user edits. Real payroll values always come from the backend engine.
 *
 * Supported syntax (matching the documented derived-variable examples):
 *   + - * / ( )   arithmetic
 *   40%           percent literal  → (40/100)
 *   min/max/round/abs/floor/ceil/sqrt(...)
 *   references to other variable codes (BASIC, MONTHLY_CTC, …)
 */

const FUNCS = {
  min: Math.min,
  max: Math.max,
  round: Math.round,
  abs: Math.abs,
  floor: Math.floor,
  ceil: Math.ceil,
  sqrt: Math.sqrt,
};

export function preprocessExpression(expr) {
  if (!expr) return "";
  // number% → (number/100)
  return String(expr).replace(/(\d+(?:\.\d+)?)\s*%/g, "($1/100)");
}

/** Evaluate a single expression against a scope of {code: number}. */
export function evaluateExpression(expr, scope = {}) {
  const pre = preprocessExpression(expr);
  if (!pre.trim()) return { ok: false };
  const full = { ...FUNCS, ...scope };
  const names = Object.keys(full);
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...names, `"use strict"; return (${pre});`);
    const val = fn(...names.map((n) => full[n]));
    if (typeof val === "number" && Number.isFinite(val)) return { ok: true, value: val };
    return { ok: false };
  } catch {
    return { ok: false };
  }
}

/**
 * Resolve every derived variable by repeated passes (handles dependencies like
 * HRA → BASIC → MONTHLY_CTC). Variables that reference unknown identifiers are
 * simply left unresolved.
 */
export function evaluateAll(derivedRows, baseScope = {}) {
  const scope = { ...baseScope };
  const resolved = {};
  const pending = (derivedRows || []).filter((r) => r?.code && r?.expression);
  const maxPasses = pending.length + 3;
  let changed = true;
  let passes = 0;
  while (changed && passes < maxPasses) {
    changed = false;
    passes += 1;
    for (const r of pending) {
      const code = String(r.code).trim();
      if (code in resolved) continue;
      const res = evaluateExpression(r.expression, scope);
      if (res.ok) {
        resolved[code] = res.value;
        scope[code] = res.value;
        changed = true;
      }
    }
  }
  return { scope, resolved };
}

/** Identifiers referenced by an expression (uppercased), excluding functions. */
export function referencedIdentifiers(expr) {
  if (!expr) return [];
  const matches = String(expr).match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
  const fns = new Set(Object.keys(FUNCS));
  return [...new Set(matches.filter((m) => !fns.has(m.toLowerCase())))];
}
